import { usePortalMessage } from "../i18n/message.js";
import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The verified-domain offer to join (owner answer 47, 2026-10-06): after sign-in, a person whose verified email is at
 * an organization's verified domain with auto-join on — and who holds nothing there — is asked, in the owner's words,
 * "Your colleagues at <org> use WeTalk — join?". They join with the organization's constrained role (never Owner or
 * Admin) only if they press Join; the console then switches into that organization (`useSwitchScope`, which asks
 * first when this or another tab holds unsaved edits).
 *
 * "Not now" hides that offer in this browser (ids only, in `localStorage`, read and written inside try/catch — a
 * private window simply asks again); nothing is stored on the server for it. Drawn above the page like the post-switch
 * notice, with the prototype's tokens (`c-join-offer` in shell.css). Never shown to a WeTalk operator acting as the
 * person (the API refuses them the offers).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Icon } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiProblemText } from "../api/api-error.js";
import { useGateway } from "../api/provider.js";
import { QUERY_KEY } from "../api/query.js";
import type { DomainJoinOfferView } from "../api/slice/domain-join-offer.js";
import { WORKSPACE_ROLE_WORD } from "../routes/account-admin/page/role-copy.js";
import { useSwitchScope } from "../scope/guard.js";
import { GLYPH } from "./glyph.js";

const DISMISSED_KEY_PREFIX = "wetalk.domain-join-offer.dismissed.";

function readDismissed(personId: string): readonly string[] {
  try {
    const raw = globalThis.localStorage.getItem(`${DISMISSED_KEY_PREFIX}${personId}`);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((one): one is string => typeof one === "string") : [];
  } catch {
    return [];
  }
}

function writeDismissed(personId: string, offerId: readonly string[]): void {
  try {
    globalThis.localStorage.setItem(`${DISMISSED_KEY_PREFIX}${personId}`, JSON.stringify(offerId));
  } catch {
    // A browser that keeps nothing asks again next time; the offer itself is unchanged.
  }
}

/** "Main as Viewer" / "Main as Viewer and Support as Developer". */
export function joinedWorkspaceText(offer: DomainJoinOfferView): string {
  const part = offer.workspace.map((one) => portalText("shell", "value_as_value_504f96b1", { v0: String(one.name), v1: String(WORKSPACE_ROLE_WORD[one.memberRole]) }));
  if (part.length <= 1) return part.join("");
  return portalText("shell", "value_and_value_dcf74a8c", { v0: String(part.slice(0, -1).join(", ")), v1: String(part.at(-1) ?? "") });
}

export interface DomainJoinOfferBannerProps {
  readonly personId: string;
  /** `false` while a WeTalk operator acts as the person: no offer is asked for or drawn. */
  readonly enabled: boolean;
}

export function DomainJoinOfferBanner({ personId, enabled }: DomainJoinOfferBannerProps): ReactNode {
  usePortalTranslation("shell");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const scope = useSwitchScope();
  const [dismissed, setDismissed] = useState<readonly string[]>(() => readDismissed(personId));
  const [problem, setProblem] = usePortalMessage();
  const offers = useQuery({
    queryKey: [...QUERY_KEY.domainJoinOffer, personId],
    queryFn: () => gateway.listDomainJoinOffers(),
    enabled,
    retry: false,
    staleTime: 300_000,
  });
  const accept = useMutation({
    mutationFn: (offer: DomainJoinOfferView) => gateway.acceptDomainJoinOffer(offer.offerId),
    onSuccess: async (accepted) => {
      setProblem(null);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.domainJoinOffer });
      scope.switchTo({ accountId: accepted.accountId, workspaceId: accepted.landingWorkspaceId });
    },
    onError: async (raised: Error) => {
      setProblem(() => apiProblemText(raised, portalText("shell", "you_could_not_join_just_now_07d6b32e")));
      await offers.refetch();
    },
  });

  const offer = enabled ? offers.data?.find((one) => !dismissed.includes(one.offerId)) : undefined;
  if (offer === undefined) return null;

  const notNow = (): void => {
    const next = [...dismissed, offer.offerId];
    setDismissed(next);
    setProblem(null);
    writeDismissed(personId, next);
  };

  return (
    <section className="c-join-offer" aria-labelledby="c-join-offer-title">
      <Icon glyph={GLYPH.people} size="lg" />
      <div className="c-join-offer-text">
        <p id="c-join-offer-title" className="c-join-offer-title">
          {portalText("shell", "your_colleagues_at_33325078") + " "}{offer.accountName} {portalText("shell", "use_wetalk_join_fa6048c0") + " "}</p>
        <p className="c-join-offer-detail">
          {portalText("shell", "your_email_address_is_at_4b7665e9") + " "}{offer.domain}{portalText("shell", "which_1579175d") + " "}{offer.accountName} {portalText("shell", "has_verified_you_would_join_422b038a")}{" "}
          {joinedWorkspaceText(offer)}{portalText("shell", "nothing_changes_unless_you_join_aae514da") + " "}</p>
        {problem === null ? null : (
          <p className="c-join-offer-problem" role="alert">
            {problem}
          </p>
        )}
      </div>
      <span className="c-join-offer-action">
        <Button
          variant="primary"
          disabled={accept.isPending || scope.busy}
          onClick={() => {
            accept.mutate(offer);
          }}
        >
          {accept.isPending ? portalText("shell", "joining_f44c1e42") : portalText("shell", "join_value_fe518cc9", { v0: String(offer.accountName) })}
        </Button>
        <Button variant="ghost" disabled={accept.isPending} onClick={notNow}>
          {portalText("shell", "not_now_e4571490") + " "}</Button>
      </span>
    </section>
  );
}
